/**
 * Ano Sync selector — reads the channel's recent messages and projects the
 * ones carrying an `ano_sync` payload into Sync Event records + the derived
 * Control Tower state. Reuses the existing `messages.byChannelComposite`
 * subscription shape (Zero dedupes identical (channelId, limit) view keys).
 *
 * The reduction itself (`deriveControlTowerState`) is shared with the server
 * GET /context endpoint so the panel and an agent's pre-work read agree.
 */
import { useMemo } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import { extractSyncEvent } from "@ano/shared/sync/sync-event";
import {
  deriveControlTowerState,
  type ControlTowerState,
  type SyncEventRecord,
} from "@ano/shared/sync/control-tower";

/** How many recent messages to scan for Sync Events (MVP: recent window). */
export const SYNC_EVENT_SCAN_LIMIT = 100;

export interface ChannelSyncEvents {
  records: SyncEventRecord[];
  state: ControlTowerState;
}

/** Pure projection — extracted so it's unit-testable without a Zero client. */
export function messagesToSyncRecords(
  rows: ReadonlyArray<{
    id: string;
    payload: Record<string, unknown> | null;
    created_at: number;
  }>,
): SyncEventRecord[] {
  const records: SyncEventRecord[] = [];
  for (const m of rows) {
    const event = extractSyncEvent(m.payload);
    if (!event) continue;
    records.push({
      ...event,
      messageId: m.id,
      createdAt: Number(m.created_at),
    });
  }
  return records;
}

export function useChannelSyncEvents(
  channelId: string | null,
): ChannelSyncEvents {
  const [messages] = useQuery(
    channelId
      ? queries.messages.byChannelComposite({
          channelId,
          limit: SYNC_EVENT_SCAN_LIMIT,
        })
      : null,
  );

  return useMemo(() => {
    const rows = (messages ?? []) as ReadonlyArray<{
      id: string;
      payload: Record<string, unknown> | null;
      created_at: number;
    }>;
    const records = messagesToSyncRecords(rows);
    return { records, state: deriveControlTowerState(records) };
  }, [messages]);
}
